/**
 * File tree shaping.
 *
 * The listing endpoint returns a flat array that is *not* in tree order -- it
 * can list `nested/inner` before `nested` -- so rendering it verbatim produced a
 * jumbled list where a collapsed folder still appeared to contain items.
 *
 * This module turns that flat list into a parent-before-child sequence and
 * applies collapse state. It is pure so the rules can be tested directly
 * instead of inferred from screenshots.
 */

export interface TreeEntry {
  readonly path: string;
  readonly name: string;
  readonly kind: 'file' | 'directory' | 'symlink' | 'other';
  readonly size: number;
  readonly modifiedAt: string;
}

export interface TreeNode {
  readonly entry: TreeEntry;
  readonly depth: number;
}

/** Accept either separator so Windows-style paths behave the same. */
function normalise(path: string): string {
  return path.replace(/\\/g, '/').replace(/^\/+/, '');
}

function parentOf(path: string): string | null {
  const clean = normalise(path);
  const index = clean.lastIndexOf('/');
  if (index <= 0) return null;
  return clean.slice(0, index);
}

/** Directories first, then case-insensitive name order. */
function compare(a: TreeEntry, b: TreeEntry): number {
  const aDir = a.kind === 'directory';
  const bDir = b.kind === 'directory';
  if (aDir !== bDir) return aDir ? -1 : 1;

  const byName = a.name.localeCompare(b.name, undefined, { sensitivity: 'base', numeric: true });
  if (byName !== 0) return byName;
  return a.path.localeCompare(b.path);
}

/**
 * Rows to render.
 *
 * When a filter is active, matches are listed regardless of collapse state --
 * that is the point of filtering -- but they are still ordered so a parent
 * never trails its own child. Otherwise the tree is walked depth-first and a
 * collapsed folder hides its entire subtree at every depth.
 */
export function visibleTree(
  entries: readonly TreeEntry[],
  collapsed: ReadonlySet<string>,
  filter = '',
): TreeNode[] {
  const byPath = new Map<string, TreeEntry>();
  for (const entry of entries) byPath.set(normalise(entry.path), entry);

  const childrenOf = new Map<string | null, TreeEntry[]>();
  for (const [path, entry] of byPath) {
    const parent = parentOf(path);
    // The listing is depth-limited, so a child can arrive without its parent.
    // Treat that as a root rather than dropping the entry on the floor.
    const key = parent !== null && byPath.has(parent) ? parent : null;
    const bucket = childrenOf.get(key) ?? [];
    bucket.push(entry);
    childrenOf.set(key, bucket);
  }
  for (const bucket of childrenOf.values()) bucket.sort(compare);

  const depthOf = (entry: TreeEntry): number => {
    const clean = normalise(entry.path);
    return (clean.match(/\//g) ?? []).length;
  };

  if (filter.trim()) {
    const needle = filter.trim().toLowerCase();
    const matches = [...byPath.values()]
      .filter((entry) => entry.name.toLowerCase().includes(needle))
      .sort((a, b) => {
        // Order by path so ancestors stay above descendants, then by name.
        const byPathOrder = normalise(a.path).localeCompare(normalise(b.path));
        if (byPathOrder !== 0) return byPathOrder;
        return compare(a, b);
      });
    return matches.map((entry) => ({ entry, depth: depthOf(entry) }));
  }

  const out: TreeNode[] = [];
  const walk = (parent: string | null, depth: number) => {
    for (const entry of childrenOf.get(parent) ?? []) {
      const path = normalise(entry.path);
      out.push({ entry, depth });
      // A collapsed folder hides everything beneath it, not just its
      // immediate children.
      if (entry.kind === 'directory' && !collapsed.has(path)) {
        walk(path, depth + 1);
      }
    }
  };
  walk(null, 0);
  return out;
}

/** Toggle a folder in the collapsed set. */
export function toggleCollapsed(
  collapsed: ReadonlySet<string>,
  path: string,
): ReadonlySet<string> {
  const key = normalise(path);
  const next = new Set(collapsed);
  if (next.has(key)) next.delete(key);
  else next.add(key);
  return next;
}

/** True when the folder is currently collapsed. */
export function isCollapsed(collapsed: ReadonlySet<string>, path: string): boolean {
  return collapsed.has(normalise(path));
}